import React from 'react';
import { CleanPromotion } from '../../../export-types/cleaned-types';
import Table, { CellProps } from 'rsuite/Table';
import Whisper from 'rsuite/Whisper';
import IconButton from 'rsuite/IconButton';
import { WhisperSpeaker } from '../../../utils/whisper-speaker';
import Popover from 'rsuite/Popover';
import Dropdown from 'rsuite/Dropdown';
import { ModalsService } from '../../../services/modals.service';
import { usePaginationQs } from '../../../utils/usePagination';
import Pagination from 'rsuite/Pagination';
import MyLoader from '../../common/loader.component';

import { RouterOutput, trpcSwr } from '../../../api/trpc';
import moment from 'moment';
import { SendExampleModal } from './SendExampleModal';

import { AddEmailCampaign } from './AddEmailCampaign';

export type ItemEmailCampaign =
  RouterOutput['emailCampaign']['list']['items'][number];

export function getStatusEmailCampaign(
  startAt: Date,
  isPaused: boolean,
  recipients: string,
  sentCount: number,
  finishedAt?: Date | null,
): {
  status: string;
  name: string;
  statusClass: string;
} {
  const currentDate = new Date();
  const startDate = new Date(startAt);
  const recipientsCount = recipients.split(',').length;

  let status;
  let name;
  let statusClass;

  if (finishedAt) {
    status = 'stopped';
    name = 'Завершено';
    statusClass = 'text-danger';
  } else if (isPaused || currentDate < startDate) {
    status = 'not_started';
    name = 'Ожидание запуска';
    statusClass = 'text-muted';
  } else if (isPaused && sentCount > 0) {
    status = 'paused';
    name = 'Приостановлено';
    statusClass = 'text-orange';
  } else {
    status = 'running';
    name = 'В процессе';
    statusClass = 'text-success';
  }

  return { status, name, statusClass };
}

export const EmailCampaignTab = () => {
  const { take, skip, handleSetPage, handleChangeLimit, page } =
    usePaginationQs(20);

  const { data, error, isLoading, mutate } = trpcSwr.emailCampaign.list.useSWR<{
    items: CleanPromotion[];
    total: number;
  }>(
    {
      take,
      skip,
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  const renderMenu =
    (emailCampaign: ItemEmailCampaign): WhisperSpeaker =>
    ({ onClose, left, top, className }, ref) => {
      const handleSelect = (eventKey: number | string | undefined) => {
        onClose();
        if (eventKey === 'edit') {
          ModalsService.createModal(AddEmailCampaign, { emailCampaign }).then(
            () => mutate(),
          );
        } else if (eventKey === 'sendExample') {
          ModalsService.createModal(SendExampleModal, {
            emailCampaign,
          }).then(() => mutate());
        }
      };
      return (
        <Popover ref={ref} className={className} style={{ left, top }} full>
          <Dropdown.Menu onSelect={handleSelect}>
            <Dropdown.Item eventKey={'edit'}>
              <i className="bi bi-pencil" />
              <span className="ps-2">Изменить</span>
            </Dropdown.Item>
            <Dropdown.Item eventKey={'sendExample'}>
              <i className="bi bi-envelope"></i>
              <span className="ps-2">Отправить тестовое письмо</span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Popover>
      );
    };

  const ActionCell = ({
    rowData,
    dataKey,
    ...props
  }: CellProps<ItemEmailCampaign>) => {
    return (
      rowData && (
        <Table.Cell
          {...props}
          className="link-group no-padding-cell pt-2"
          align="right"
        >
          <Whisper
            placement="leftStart"
            trigger="click"
            speaker={renderMenu(rowData)}
          >
            <IconButton
              appearance="subtle"
              icon={<i className="bi bi-three-dots" />}
            />
          </Whisper>
        </Table.Cell>
      )
    );
  };

  const DataCell = ({
    rowData,
    dataKey,
    ...props
  }: CellProps<ItemEmailCampaign>) => {
    return (
      rowData && (
        <Table.Cell {...props}>
          {moment(rowData.startAt).format('DD.MM.YYYY HH:mm')}
        </Table.Cell>
      )
    );
  };

  const RecipientsCell = ({
    rowData,
    dataKey,
    ...props
  }: CellProps<ItemEmailCampaign>) => {
    return (
      rowData && (
        <Table.Cell {...props}>
          {rowData.recipients
            ? rowData.recipients.split(',').length
            : 'Все пользователи'}
        </Table.Cell>
      )
    );
  };

  const SentCountCell = ({
    rowData,
    dataKey,
    ...props
  }: CellProps<ItemEmailCampaign>) => {
    return (
      rowData && <Table.Cell {...props}>{rowData._count.sent ?? 0}</Table.Cell>
    );
  };

  const StatusCell = ({
    rowData,
    dataKey,
    ...props
  }: CellProps<ItemEmailCampaign>) => {
    if (!rowData || !rowData.startAt) return null;

    const resultStatus = getStatusEmailCampaign(
      rowData.startAt,
      rowData.isPaused,
      rowData.recipients,
      rowData._count.sent,
      rowData.finishedAt,
    );

    return (
      <Table.Cell {...props} className={resultStatus.statusClass}>
        {resultStatus.name}
      </Table.Cell>
    );
  };

  if ((error || !data) && !isLoading) {
    return (
      <>
        <div className="pb-4">Неизвестная ошибка</div>
      </>
    );
  }

  return (
    <div>
      {isLoading ? (
        <MyLoader />
      ) : (
        <>
          {' '}
          <Table
            loading={isLoading}
            data={data?.items}
            headerHeight={57}
            autoHeight
            rowKey="id"
            locale={{
              loading: 'Загрузка',
              emptyMessage: 'Нет рассылок',
            }}
          >
            <Table.Column flexGrow={1}>
              <Table.HeaderCell fullText>Название</Table.HeaderCell>
              <Table.Cell dataKey="name" />
            </Table.Column>
            <Table.Column flexGrow={1}>
              <Table.HeaderCell fullText>Начало действия</Table.HeaderCell>
              <DataCell />
            </Table.Column>
            <Table.Column flexGrow={1}>
              <Table.HeaderCell fullText>Получателей</Table.HeaderCell>
              <RecipientsCell />
            </Table.Column>
            <Table.Column flexGrow={1}>
              <Table.HeaderCell fullText>
                <div>
                  Отправлено
                  <span className="small text-muted d-block">
                    (Клиенты с почтой и согласием на рассылку)
                  </span>
                </div>
              </Table.HeaderCell>
              <SentCountCell />
            </Table.Column>
            <Table.Column flexGrow={1} minWidth={100}>
              <Table.HeaderCell fullText>Статус</Table.HeaderCell>
              <StatusCell />
            </Table.Column>
            <Table.Column>
              <Table.HeaderCell> </Table.HeaderCell>
              <ActionCell />
            </Table.Column>
          </Table>
          {data && data.total > take && (
            <Pagination
              className="mt-2 pb-4"
              prev
              next
              first
              last
              ellipsis
              boundaryLinks
              maxButtons={5}
              size="md"
              layout={['pager']}
              total={data.total}
              limit={take}
              activePage={page}
              onChangePage={handleSetPage}
              onChangeLimit={handleChangeLimit}
            />
          )}
        </>
      )}
    </div>
  );
};
